import { useQuery } from '@tanstack/react-query';
import { supabase } from '@vehix/shared/admin/portal-bridge';

/**
 * Pending-user-approvals count for the admin dashboard's "Pending Approvals" widget.
 *
 * Uses the same SECURITY DEFINER RPC as UserApprovalsTab so RLS is bypassed
 * for super admins. We filter client-side because:
 *   1. The approvals queue is bounded (admin onboarding queue, not a high-cardinality table)
 *   2. Adding a dedicated count RPC would require a DB migration (out of scope here)
 *   3. UserApprovalsTab already pulls the full set, so cache reuse via React Query
 *      is possible by sharing the queryKey shape with that component later
 */
interface ApprovalRow {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
}

const QUERY_KEY = ['admin', 'user-approvals', 'pending-count'] as const;

async function fetchPendingApprovalsCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_all_user_approvals_for_admin');
  if (error) throw error;
  if (!Array.isArray(data)) return 0;
  return (data as ApprovalRow[]).filter(row => row.status === 'pending').length;
}

export function useUserApprovalsCount() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchPendingApprovalsCount,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1
  });
}
